import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createPortal } from "react-dom";
import { fetchClinicians } from "../../store/thunks/cliniciansThunk";
import { fetchAppointmentsByDateRangeOnly } from "../../store/thunks/appointmentsThunk";
import { fetchMeetingsForCurrentWeek } from "../../store/thunks/meetingsThunk";
import { setSelectedAppointment } from "../../store/slices/appointmentsSlice";
import { setSelectedMeeting } from "../../store/slices/meetingSlice";
import { fetchClinics } from "../../store/thunks/clinicThunk";
import { setSelectedClinicsForSchedule } from "../../store/slices/clinicSlice";
import "./SevenDayCalendarV2.css";
import AppointmentSummary from "../../applicationUi/components/appointments/AppointmentSummary";
import QuickAddMeeting from "../../modals/QuickAddMeeting";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { format, addDays, startOfWeek, parseISO, addMinutes } from "date-fns";
import { toDate } from "date-fns-tz";
import MultiSelect from "../../applicationUi/components/MultiSelect";
import axios from "axios";
import {
  getCalendarFeedUrl,
  regenerateCalendarFeedUrl,
} from "../../store/thunks/calendarFeedThunk";
import { resetUrlUpdated } from "../../store/slices/calendarFeedSlice";

export default function SevenDayCalendarV2() {
  const dispatch = useDispatch();
  const sevenDayAppointments = useSelector(
    (state) => state.appointments.sevenDayAppointments
  );
  const sevenDayMeetings = useSelector((state) => state.meetings.meetings);
  const stateClinicians = useSelector(
    (state) => state.clinicians.clinicians || []
  );
  const user = useSelector((state) => state.auth.user);
  const clinics = useSelector((state) => state.clinics.clinics || []);
  const selectedClinicsForSchedule = useSelector(
    (state) => state.clinics.selectedClinicsForSchedule || []
  );
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedClinician, setSelectedClinician] = useState(null);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [hideCancelled, setHideCancelled] = useState(true);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [showCalendarFeedModal, setShowCalendarFeedModal] = useState(false);
  const calendarFeedUrl = useSelector(
    (state) => state.calendarFeed.calendarFeedUrl
  );
  const calendarFeedLoading = useSelector(
    (state) => state.calendarFeed.loading
  );
  const calendarFeedError = useSelector((state) => state.calendarFeed.error);
  const urlUpdated = useSelector((state) => state.calendarFeed.urlUpdated);

  const clinicians = stateClinicians.filter(
    (clinician) => clinician.userStatus === "active"
  );

  const inactiveClinicians = stateClinicians.filter(
    (clinician) => clinician.userStatus === "inactive"
  );

  const container = useRef(null);
  const containerNav = useRef(null);
  const containerOffset = useRef(null);
  const [showWorkingHoursOnly, setShowWorkingHoursOnly] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredItem, setHoveredItem] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipTimeout, setTooltipTimeout] = useState(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  const [isQuickAddMeetingOpen, setIsQuickAddMeetingOpen] = useState(false);
  const [isAppointmentSummaryOpen, setIsAppointmentSummaryOpen] =
    useState(false);

  // Add toast state
  const [showCopyToast, setShowCopyToast] = useState(false);

  // Add minimized header state - defaults to minimized
  const [isHeaderMinimized, setIsHeaderMinimized] = useState(true);

  // Modal states
  const [showClinicModal, setShowClinicModal] = useState(false);
  const [showClinicianModal, setShowClinicianModal] = useState(false);

  const startDate = startOfWeek(currentDate, { weekStartsOn: 0 });

  // Generate week days
  const weekDays = Array.from({ length: 7 }, (_, index) => {
    const date = addDays(startDate, index);
    const isToday =
      format(date, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");

    return {
      shortDay: format(date, "E"), // Mon, Tue, etc.
      fullDay: format(date, "EEE"), // Monday, Tuesday, etc.
      date: format(date, "d"), // 1-31
      isToday,
    };
  });

  // Calculate the hours to display based on the toggle
  const hoursToDisplay = showWorkingHoursOnly ? [8, 20] : [0, 24]; // 8 AM to 8 PM or 12 AM to 12 AM

  // Adjust the rendering of the time slots
  const renderTimeSlots = () => {
    const timeSlots = [];
    for (let hour = hoursToDisplay[0]; hour < hoursToDisplay[1]; hour++) {
      timeSlots.push(
        <div key={hour} className="time-slot">
          {hour}:00
        </div>
      );
    }
    return timeSlots;
  };

  useEffect(() => {
    const currentMinute = new Date().getHours() * 60;
    container.current.scrollTop =
      ((container.current.scrollHeight -
        containerNav.current.offsetHeight -
        containerOffset.current.offsetHeight) *
        currentMinute) /
      1440;
  }, []);

  const handlePreviousWeek = () => {
    setCurrentDate((prevDate) => addDays(prevDate, -7));
  };

  const handleNextWeek = () => {
    setCurrentDate((prevDate) => addDays(prevDate, 7));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getCurrentWeek = (date) => {
    const week = [];
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() - date.getDay());
    startOfWeek.setHours(0, 0, 0, 0); // Set to start of day (midnight)

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  // Load clinics on mount
  useEffect(() => {
    dispatch(fetchClinics());
    dispatch(fetchClinicians());
  }, [dispatch]);

  // Fetch appointments when date or clinic selection changes
  useEffect(() => {
    const currentWeek = getCurrentWeek(currentDate);
    const startDate = new Date(currentWeek[0]);
    startDate.setHours(0, 1, 0, 0); // Ensure startDate is at 12:01 AM

    const endDate = new Date(currentWeek[6]);
    endDate.setHours(23, 59, 59, 999); // Ensure endDate is at 11:59 PM

    // Support filtering by selected clinics
    // If only one clinic is selected, pass it as clinicId for server-side filtering
    // If multiple or none selected, let client-side filtering handle it
    const clinicId =
      selectedClinicsForSchedule.length === 1
        ? selectedClinicsForSchedule[0]._id
        : null;

    dispatch(
      fetchAppointmentsByDateRangeOnly({ startDate, endDate, clinicId })
    );
    dispatch(fetchMeetingsForCurrentWeek({ startDate, endDate }));
  }, [
    dispatch,
    currentDate,
    isQuickAddMeetingOpen,
    isAppointmentSummaryOpen,
    selectedClinicsForSchedule,
  ]);

  const calculateAppointmentGridPosition = (startTime, endTime) => {
    // Convert UTC to local time
    const localStart = toDate(parseISO(startTime), {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    const localEnd = toDate(parseISO(endTime), {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    // Calculate grid row (each hour = 12 rows, each row = 5 minutes)
    const startHour = localStart.getHours();
    const startMinutes = localStart.getMinutes();
    const startRow = showWorkingHoursOnly
      ? (startHour - 8) * 12 + Math.floor(startMinutes / 5) + 1 // Adjust for working hours
      : startHour * 12 + Math.floor(startMinutes / 5) + 1; // Full 24 hours

    // Calculate duration in 5-minute increments
    const durationMinutes = (localEnd - localStart) / (1000 * 60);
    const span = Math.ceil(durationMinutes / 5);
    return { startRow, span };
  };

  const calculateMeetingGridPosition = (date, startTime, duration) => {
    if (!date || !startTime || startTime === "Invalid Date") {
      console.error("Invalid date or startTime:", { date, startTime });
      return { startRow: 0, span: 0 };
    }

    const fullDateTime = parseISO(date.split("T")[0] + "T" + startTime);
    const localStart = toDate(fullDateTime, {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    const startHour = localStart.getHours();
    const startMinutes = localStart.getMinutes();
    const startRow = showWorkingHoursOnly
      ? (startHour - 8) * 12 + Math.floor(startMinutes / 5) + 1
      : startHour * 12 + Math.floor(startMinutes / 5) + 1;

    const span = Math.ceil(duration / 5);
    return { startRow, span };
  };

  // Add this helper function to find overlapping events
  const findOverlappingEvents = (currentEvent, allEvents) => {
    const currentStart = parseISO(currentEvent.start || currentEvent.date);
    const currentEnd = currentEvent.end
      ? parseISO(currentEvent.end)
      : addMinutes(currentStart, currentEvent.duration || 0);

    return allEvents.filter((event) => {
      if (event._id === currentEvent._id) return false;

      const eventStart = parseISO(event.start || event.date);
      const eventEnd = event.end
        ? parseISO(event.end)
        : addMinutes(eventStart, event.duration || 0);

      return currentStart < eventEnd && currentEnd > eventStart;
    });
  };

  const handleEventClick = (event) => {
    if ("client" in event) {
      dispatch(setSelectedAppointment(event));
      // setSelectedEvent(event);
      setIsAppointmentSummaryOpen(true);
    } else {
      dispatch(setSelectedMeeting(event));
      setSelectedEvent(event);
      setIsQuickAddMeetingOpen(true);
    }
  };

  // Add this component for the enhanced tooltip
  const EnhancedTooltip = ({
    mainEvent,
    overlappingEvents,
    onEventHover,
    onEventLeave,
    onEventClick,
  }) => {
    const isMainEventAppointment = "client" in mainEvent;
    const otherEvents = overlappingEvents.sort((a, b) => {
      const aStart = parseISO(a.start || a.date);
      const bStart = parseISO(b.start || b.date);
      return aStart - bStart;
    });

    const renderEventContent = (event, isHovered = false) => {
      const isAppointment = "client" in event;
      const start = parseISO(event.start || event.date);

      return (
        <div
          key={event._id}
          className={`p-2 rounded-sm relative group cursor-pointer ${
            isHovered ? "bg-gray-50 ring-1 ring-gray-200" : ""
          }`}
          style={{
            backgroundColor: isAppointment
              ? `${event.clinician.calendarColor}20`
              : "#6B728020",
            border: isAppointment ? "none" : "1px dashed #6B7280",
          }}
          onMouseEnter={() => onEventHover(event)}
          onMouseLeave={onEventLeave}
          onClick={() => onEventClick(event)}>
          <div className="flex justify-between items-start">
            <div>
              <div
                className="font-medium text-sm truncate"
                style={{
                  color: isAppointment
                    ? event.clinician.calendarColor
                    : "#374151",
                }}>
                {isAppointment
                  ? format(parseISO(event.start || event.date), "h:mm a") +
                    ` - ${event.clinician.calendarDisplayName}`
                  : `${format(
                      parseISO(
                        event.date.split("T")[0] + "T" + event.startTime
                      ),
                      "h:mm a"
                    )} - Meeting`}
              </div>
              <div className="flex text-xs text-gray-600 truncate">
                {event.blockNumber && (
                  <span
                    style={{
                      color: isAppointment
                        ? event.clinician.calendarColor
                        : "#374151",
                    }}>
                    Block #{event.blockNumber}-{" "}
                  </span>
                )}
                <span
                  style={{
                    color: isAppointment
                      ? event.clinician.calendarColor
                      : "#374151",
                  }}>
                  {event.title}
                </span>
              </div>
              <div
                className="text-xs"
                style={{
                  color: isAppointment
                    ? event.clinician.calendarColor
                    : "#374151",
                }}>
                {isAppointment
                  ? `${event.client?.firstName} ${event.client?.lastName}`
                  : event.attendees.map((attendee, index) => (
                      <span key={attendee._id}>
                        {`${attendee?.firstName} ${attendee?.lastName}`}
                        {index < event.attendees.length - 1 ? ", " : ""}
                      </span>
                    ))}
              </div>
            </div>
          </div>
        </div>
      );
    };

    return (
      <div
        className="absolute z-50"
        style={{
          top: "-0.5rem",
          left: "50%",
          transform: "translate(-50%, -100%)",
          width: "max-content",
          maxWidth: "300px",
        }}>
        <div
          className="bg-white p-2 rounded-sm shadow-lg border border-gray-200"
          style={{
            position: "relative",
          }}>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 size-2 bg-white border-r border-b border-gray-200" />

          {/* Hovered Event Section */}
          <div>
            <div className="text-sm font-semibold mb-1 pb-1 border-b border-gray-100">
              Hovered Event
            </div>
            {renderEventContent(mainEvent, true)}
          </div>

          {/* Concurrent Events Section */}
          {otherEvents.length > 0 && (
            <div className="mt-3">
              <div className="text-sm font-semibold mb-1 pb-1 border-b border-gray-100">
                Concurrent Events ({otherEvents.length})
              </div>
              <div className="space-y-1">
                {otherEvents.map((event) => renderEventContent(event))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const isUserClinician = clinicians.find(
    (clinician) => clinician._id === user._id
  );

  const [selectedClinicians, setSelectedClinicians] = useState(
    isUserClinician ? [isUserClinician] : []
  );

  // Debug logging - after all state is declared
  useEffect(() => {
    console.log('[Debug] Clinics data:', clinics);
    console.log('[Debug] Clinicians data:', clinicians);
    console.log('[Debug] selectedClinicsForSchedule:', selectedClinicsForSchedule);
    console.log('[Debug] selectedClinicians:', selectedClinicians);
  }, [clinics, clinicians, selectedClinicsForSchedule, selectedClinicians]);

  const handleRemoveClinician = (clinicianId) => {
    setSelectedClinicians((prev) =>
      prev.filter((clinician) => clinician._id !== clinicianId)
    );
  };

  const calculateEventPosition = (event, allEvents) => {
    // Handle different datetime formats for appointments vs meetings
    let eventStart, eventEnd;

    if (event.start) {
      // Appointment - has start/end times
      eventStart = parseISO(event.start);
      eventEnd = event.end
        ? parseISO(event.end)
        : addMinutes(eventStart, event.duration || 0);
    } else if (event.date && event.startTime) {
      // Meeting - has date + startTime + duration
      const fullDateTime = parseISO(
        event.date.split("T")[0] + "T" + event.startTime
      );
      eventStart = toDate(fullDateTime, {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      eventEnd = addMinutes(eventStart, event.duration || 0);
    } else {
      // Fallback
      eventStart = parseISO(event.start || event.date);
      eventEnd = event.end
        ? parseISO(event.end)
        : addMinutes(eventStart, event.duration || 0);
    }

    // Helper function to get clinician IDs involved in an event
    const getEventClinicianIds = (evt) => {
      if (evt.clinician?._id) {
        // Appointment - has direct clinician
        return [evt.clinician._id];
      } else if (evt.attendees) {
        // Meeting - extract attendee IDs
        return evt.attendees.map((attendee) => attendee._id);
      }
      return [];
    };

    const eventClinicianIds = getEventClinicianIds(event);

    // Find ALL events that overlap with this event's time range
    // Since filtering already happened at component level, we need to position all visible overlapping events
    const overlappingEvents = allEvents.filter((otherEvent) => {
      if (otherEvent._id === event._id) return false;

      // Handle different datetime formats for appointments vs meetings
      let otherStart, otherEnd;

      if (otherEvent.start) {
        // Appointment - has start/end times
        otherStart = parseISO(otherEvent.start);
        otherEnd = otherEvent.end
          ? parseISO(otherEvent.end)
          : addMinutes(otherStart, otherEvent.duration || 0);
      } else if (otherEvent.date && otherEvent.startTime) {
        // Meeting - has date + startTime + duration
        const fullDateTime = parseISO(
          otherEvent.date.split("T")[0] + "T" + otherEvent.startTime
        );
        otherStart = toDate(fullDateTime, {
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        otherEnd = addMinutes(otherStart, otherEvent.duration || 0);
      } else {
        // Fallback
        otherStart = parseISO(otherEvent.start || otherEvent.date);
        otherEnd = otherEvent.end
          ? parseISO(otherEvent.end)
          : addMinutes(otherStart, otherEvent.duration || 0);
      }

      // Check if the events overlap in time
      return eventStart < otherEnd && eventEnd > otherStart;
    });

    const allOverlappingEvents = [event, ...overlappingEvents].sort((a, b) => {
      // Sort by start time first
      const aStart = parseISO(a.start || a.date);
      const bStart = parseISO(b.start || b.date);
      if (aStart.getTime() !== bStart.getTime()) {
        return aStart - bStart;
      }
      // If start times are equal, sort by ID for consistency
      return a._id.localeCompare(b._id);
    });

    const position = allOverlappingEvents.findIndex((e) => e._id === event._id);
    const total = allOverlappingEvents.length;

    // Debug logging for datetime parsing
    if (overlappingEvents.length > 0) {
      console.log(`Event ${event._id}:`, {
        type: event.start ? "appointment" : "meeting",
        eventStart: eventStart.toISOString(),
        eventEnd: eventEnd.toISOString(),
        total,
        position,
        overlappingEvents: overlappingEvents.map((e) => ({
          id: e._id,
          type: e.start ? "appointment" : "meeting",
          start: e.start
            ? parseISO(e.start).toISOString()
            : e.date && e.startTime
            ? toDate(parseISO(e.date.split("T")[0] + "T" + e.startTime), {
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              }).toISOString()
            : "unknown",
        })),
      });
    }

    return {
      width: `${100 / total}%`,
      left: `${(position * 100) / total}%`,
      overlappingEvents,
    };
  };

  // Effect to filter appointments and meetings based on selected clinician
  useEffect(() => {
    let appointmentsToFilter = sevenDayAppointments;
    let meetingsToFilter = sevenDayMeetings;

    // Filter by clinic selection first
    if (
      selectedClinicsForSchedule.length > 0 &&
      selectedClinicsForSchedule.length < clinics.length
    ) {
      appointmentsToFilter = appointmentsToFilter.filter((appointment) =>
        selectedClinicsForSchedule.some(
          (clinic) => clinic._id === appointment.clinic?._id
        )
      );
      // Note: Meetings don't have clinic association in current schema, keeping all meetings
    }

    // Then filter by clinician selection
    if (selectedClinicians.length > 0) {
      appointmentsToFilter = appointmentsToFilter.filter((appointment) =>
        selectedClinicians.some(
          (clinician) => clinician._id === appointment.clinician._id
        )
      );

      meetingsToFilter = meetingsToFilter.filter((meeting) =>
        meeting.attendees.some((attendee) =>
          selectedClinicians.some((clinician) => clinician._id === attendee._id)
        )
      );
    }

    // Finally filter by cancelled status
    appointmentsToFilter = appointmentsToFilter.filter((appointment) => {
      if (!appointment || !Array.isArray(appointment.status)) return false;
      return hideCancelled
        ? !appointment.status.some((status) =>
            [
              "Cancelled",
              "Cancelled - No Fee",
              "Cancelled - Charge Fee",
              "Cancelled - Clinician",
              "Cancelled - Client",
              "No Show",
              "No Show - No Fee",
              "No Show - Charge Fee",
            ].includes(status)
          )
        : true;
    });

    setFilteredAppointments(appointmentsToFilter);
    setFilteredMeetings(meetingsToFilter);
  }, [
    selectedClinicians,
    selectedClinicsForSchedule,
    sevenDayAppointments,
    sevenDayMeetings,
    hideCancelled,
    clinics,
  ]);

  const renderHorizontalLines = () => {
    const lines = [];
    const startHour = showWorkingHoursOnly ? 8 : 0; // Start at 8 AM if working hours only
    const endHour = showWorkingHoursOnly ? 20 : 24; // End at 8 PM if working hours only

    for (let hour = startHour; hour < endHour; hour++) {
      lines.push(
        <div key={hour}>
          <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs/5 text-gray-400">
            {hour % 12 === 0 ? 12 : hour % 12} {hour < 12 ? "AM" : "PM"}
          </div>
        </div>
      );
      lines.push(<div key={`empty-${hour}`} />); // Empty div for spacing
    }

    return lines;
  };

  // Function to get or generate calendar feed URL
  const handleGetCalendarFeedUrl = async () => {
    try {
      await dispatch(getCalendarFeedUrl());
      setShowCalendarFeedModal(true);
    } catch (error) {
      console.error("Error getting calendar feed URL:", error);
    }
  };

  // Function to regenerate calendar feed URL
  const handleRegenerateCalendarFeedUrl = async () => {
    try {
      await dispatch(regenerateCalendarFeedUrl());
    } catch (error) {
      console.error("Error regenerating calendar feed URL:", error);
    }
  };

  // Reset urlUpdated flag when modal is closed
  useEffect(() => {
    if (!showCalendarFeedModal && urlUpdated) {
      dispatch(resetUrlUpdated());
    }
  }, [showCalendarFeedModal, urlUpdated, dispatch]);

  const TOOLTIP_SHOW_DELAY = 500; // 500ms delay before showing tooltip
  const TOOLTIP_HIDE_DELAY = 300; // 300ms delay before hiding tooltip

  const handleEventMouseEnter = (event, type, overlappingEvents) => {
    // Clear any existing timeout
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
    }

    // Set a new timeout to show the tooltip
    const timeout = setTimeout(() => {
      setHoveredItem({
        type,
        data: event,
        overlappingEvents,
      });
      setIsTooltipVisible(true);
    }, TOOLTIP_SHOW_DELAY);

    setTooltipTimeout(timeout);
  };

  const handleEventMouseLeave = (e) => {
    // Clear show timeout if it exists
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
    }

    // Set hide timeout
    const timeout = setTimeout(() => {
      // Only hide if we're not hovering over the tooltip or another event
      if (
        !e.relatedTarget?.closest(".enhanced-tooltip") &&
        !e.relatedTarget?.closest(".group")
      ) {
        setIsTooltipVisible(false);
        setHoveredItem(null);
      }
    }, TOOLTIP_HIDE_DELAY);

    setTooltipTimeout(timeout);
  };

  // Clean up timeouts when component unmounts
  useEffect(() => {
    return () => {
      if (tooltipTimeout) {
        clearTimeout(tooltipTimeout);
      }
    };
  }, [tooltipTimeout]);

  // Handle copy to clipboard with toast feedback
  const handleCopyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowCopyToast(true);
      setTimeout(() => setShowCopyToast(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <>
      <div className="flex h-full w-full flex-col seven-day-calendar">
        <header className="sticky top-0 z-30 bg-white shadow ring-1 ring-black/5">
          {/* Main Header Bar */}
          <div className="flex flex-none items-center justify-between border-b border-gray-200 px-6 py-3">
            <div className="flex items-center space-x-4 flex-1 min-w-0">
              {/* Title and Date */}
              <div className="flex items-center space-x-4 flex-shrink-0">
                <h1 className="text-lg font-semibold text-gray-900">
                  <time dateTime={format(startDate, "yyyy-MM")}>
                    {format(startDate, "MMMM yyyy")}
                  </time>
                </h1>

                {/* Navigation Controls */}
                <div className="flex items-center">
                  <div className="relative flex items-center rounded-sm bg-white shadow-sm md:items-stretch">
                    <button
                      type="button"
                      onClick={handlePreviousWeek}
                      className="flex h-8 w-10 items-center justify-center rounded-l-sm border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-8 md:pr-0 md:hover:bg-gray-50">
                      <span className="sr-only">Previous week</span>
                      <ChevronLeftIcon className="size-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={handleToday}
                      className="border-y border-gray-300 px-3 text-sm font-medium text-gray-900 hover:bg-gray-50 focus:relative">
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={handleNextWeek}
                      className="flex h-8 w-10 items-center justify-center rounded-r-sm border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-8 md:pl-0 md:hover:bg-gray-50">
                      <span className="sr-only">Next week</span>
                      <ChevronRightIcon className="size-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Filters Section - 2 Rows */}
              <div className="flex-1 min-w-0">
                <div className="space-y-2">
                  {/* Row 1: Clinics */}
                  <div className="overflow-x-auto overflow-y-visible" style={{ minHeight: '40px' }}>
                    <div className="flex items-center space-x-4" style={{ minWidth: 'max-content' }}>
                      {/* Clinic Filter Button */}
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <button
                          onClick={() => setShowClinicModal(true)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        >
                          Clinics
                        </button>
                      </div>

                      {/* Selected Clinics Display */}
                      <div className="flex items-center flex-shrink-0">
                        <div className="border border-gray-200 rounded-sm px-2 py-0.5 overflow-x-auto">
                          <div className="flex gap-1 whitespace-nowrap">
                            {selectedClinicsForSchedule.length > 0 ? (
                              selectedClinicsForSchedule.map((clinic) => (
                                <span
                                  key={clinic._id}
                                  className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-blue-50 text-blue-700 whitespace-nowrap">
                                  {clinic.name}
                                  <button
                                    type="button"
                                    className="ml-1 hover:text-red-500 hover:bg-red-50 rounded-sm p-0.5 transition-colors"
                                    onClick={() => {
                                      const newSelection =
                                        selectedClinicsForSchedule.filter(
                                          (c) => c._id !== clinic._id
                                        );
                                      dispatch(
                                        setSelectedClinicsForSchedule(newSelection)
                                      );
                                    }}>
                                    <XMarkIcon
                                      className="size-3"
                                      aria-hidden="true"
                                    />
                                  </button>
                                </span>
                              ))
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-gray-50 text-gray-600 whitespace-nowrap">
                                All Clinics
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Clinicians */}
                  <div className="overflow-x-auto overflow-y-visible" style={{ minHeight: '40px' }}>
                    <div className="flex items-center space-x-4" style={{ minWidth: 'max-content' }}>
                      {/* Clinician Filter Button */}
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <button
                          onClick={() => setShowClinicianModal(true)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        >
                          Clinicians
                        </button>
                      </div>

                      {/* Selected Clinicians Display */}
                      <div className="flex items-center flex-shrink-0">
                        <div className="border border-gray-200 rounded-sm px-2 py-0.5 overflow-x-auto">
                          <div className="flex gap-1 whitespace-nowrap">
                            {selectedClinicians.length > 0 ? (
                              selectedClinicians.map((clinician) => (
                                <span
                                  key={clinician._id}
                                  className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium whitespace-nowrap"
                                  style={{
                                    backgroundColor: `${clinician.calendarColor}20`,
                                    color: clinician.calendarColor,
                                  }}>
                                  {clinician?.firstName} {clinician?.lastName}
                                  <button
                                    type="button"
                                    className="ml-1 hover:text-red-500 hover:bg-red-50 rounded-sm p-0.5 transition-colors"
                                    onClick={() =>
                                      handleRemoveClinician(clinician._id)
                                    }>
                                    <XMarkIcon
                                      className="size-3"
                                      aria-hidden="true"
                                    />
                                  </button>
                                </span>
                              ))
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-gray-50 text-gray-600 whitespace-nowrap">
                                All Clinicians
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Hide scrollbar */}
                <style jsx>{`
                  div::-webkit-scrollbar {
                    display: none;
                  }
                `}</style>
              </div>
            </div>

            {/* Right Side Controls */}
            <div className="flex items-center space-x-3 flex-shrink-0">
              {/* Expand/Collapse Button */}
              <button
                onClick={() => setIsHeaderMinimized(!isHeaderMinimized)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-sm transition-colors"
                title={
                  isHeaderMinimized ? "Show more options" : "Show fewer options"
                }>
                <ChevronDownIcon
                  className={`w-4 h-4 transition-transform ${
                    isHeaderMinimized ? "rotate-0" : "rotate-180"
                  }`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          {/* Expandable Options Row */}
          {!isHeaderMinimized && (
            <div className="border-b border-gray-200 px-6 py-3 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={hideCancelled}
                      onChange={() => setHideCancelled(!hideCancelled)}
                      className="appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                    />
                    <p className="text-sm font-medium text-gray-700">
                      Hide Cancelled & No Shows
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={showWorkingHoursOnly}
                      onChange={() =>
                        setShowWorkingHoursOnly(!showWorkingHoursOnly)
                      }
                      className="appearance-none rounded border border-gray-300 bg-white checked:border-indigo-600 checked:bg-indigo-600 indeterminate:border-indigo-600 indeterminate:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:checked:bg-gray-100 forced-colors:appearance-auto"
                    />
                    <p className="text-sm font-medium text-gray-700">
                      Working Hours
                    </p>
                  </div>
                </div>

                {/* Subscribe button when expanded and user is clinician */}
                {isUserClinician && (
                  <button
                    onClick={handleGetCalendarFeedUrl}
                    disabled={calendarFeedLoading}
                    className="inline-flex items-center rounded-sm bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
                    {calendarFeedLoading
                      ? "Loading..."
                      : "Subscribe to Calendar"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Days Header - Simplified */}
          <div className="flex-none bg-white border-b border-gray-100 sm:pr-8">
            <div className="grid grid-cols-7 text-sm/6 text-gray-500 sm:hidden">
              {weekDays.map((day) => (
                <button
                  key={day.shortDay}
                  type="button"
                  className="flex flex-col items-center pb-2 pt-2">
                  {day.shortDay}{" "}
                  <span
                    className={`mt-1 flex size-7 items-center justify-center font-semibold text-xs
                      ${
                        day.isToday
                          ? "rounded-full bg-indigo-600 text-white"
                          : "text-gray-900"
                      }`}>
                    {day.date}
                  </span>
                </button>
              ))}
            </div>

            <div className="-mr-px hidden grid-cols-7 divide-x divide-gray-100 border-r border-gray-100 text-sm/6 text-gray-500 sm:grid">
              <div className="col-end-1 w-14" />
              {weekDays.map((day) => (
                <div
                  key={day.fullDay}
                  className="flex items-center justify-center py-2">
                  <span className="flex items-baseline">
                    {day.fullDay}
                    <span
                      className={`ml-1.5 flex items-center justify-center font-semibold text-sm
                        ${
                          day.isToday
                            ? "size-7 rounded-full bg-indigo-600 text-white"
                            : "text-gray-900"
                        }`}>
                      {day.date}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </header>
        <div
          ref={container}
          className="isolate flex flex-auto flex-col overflow-auto bg-white">
          <div
            style={{ width: "165%" }}
            className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full">
            <div
              ref={containerNav}
              className="sticky top-0 z-30 flex-none bg-white shadow ring-1 ring-black/5 sm:pr-8"></div>
            <div className="flex flex-auto">
              <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
              <div className="grid flex-auto grid-cols-1 grid-rows-1">
                {/* Horizontal lines */}
                <div
                  className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
                  style={{
                    gridTemplateRows: "repeat(48, minmax(3.5rem, 1fr))",
                  }}>
                  <div ref={containerOffset} className="row-end-1 h-8"></div>
                  {renderHorizontalLines()}
                </div>

                {/* Vertical lines */}
                <div className="col-start-1 col-end-2 row-start-1 hidden grid-cols-7 grid-rows-1 divide-x divide-gray-100 sm:grid sm:grid-cols-7">
                  <div className="col-start-1 row-span-full" />
                  <div className="col-start-2 row-span-full" />
                  <div className="col-start-3 row-span-full" />
                  <div className="col-start-4 row-span-full" />
                  <div className="col-start-5 row-span-full" />
                  <div className="col-start-6 row-span-full" />
                  <div className="col-start-7 row-span-full" />
                  <div className="col-start-8 row-span-full w-8" />
                </div>

                {/* Render Events (Appts / Meetings)*/}
                <ol
                  className="mt-4 col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-8 seven-day-calendar"
                  style={{
                    gridTemplateRows:
                      "1.75rem repeat(288, minmax(0, 1fr)) auto",
                    overflow: "auto", // Enable scrolling
                  }}>
                  {/* Render appointments */}
                  {filteredAppointments
                    .filter(
                      (appointment) =>
                        selectedClinicians.length === 0 ||
                        selectedClinicians.some(
                          (c) => c._id === appointment.clinician._id
                        )
                    )
                    .filter((appointment) => {
                      // Filter out appointments that don't belong to the current week
                      const appointmentStart = toDate(
                        parseISO(appointment.start),
                        {
                          timeZone:
                            Intl.DateTimeFormat().resolvedOptions().timeZone,
                        }
                      );
                      const appointmentDate = format(
                        appointmentStart,
                        "yyyy-MM-dd"
                      );
                      const currentWeek = getCurrentWeek(currentDate);

                      // Check if appointment date is within the current week range
                      const weekStart = format(currentWeek[0], "yyyy-MM-dd");
                      const weekEnd = format(currentWeek[6], "yyyy-MM-dd");

                      return (
                        appointmentDate >= weekStart &&
                        appointmentDate <= weekEnd
                      );
                    })
                    .map((appointment) => {
                      const { startRow, span } =
                        calculateAppointmentGridPosition(
                          appointment.start,
                          appointment.end
                        );
                      const { width, left, overlappingEvents } =
                        calculateEventPosition(appointment, [
                          ...filteredAppointments,
                          ...filteredMeetings,
                        ]);
                      const localStart = toDate(parseISO(appointment.start), {
                        timeZone:
                          Intl.DateTimeFormat().resolvedOptions().timeZone,
                      });
                      const dayOfWeek = localStart.getDay() + 1;

                      return (
                        <li
                          key={appointment._id}
                          className="relative flex"
                          style={{
                            gridRow: `${startRow} / span ${span}`,
                            gridColumnStart: dayOfWeek,
                            position: "relative",
                          }}>
                          <span
                            onMouseEnter={(e) =>
                              handleEventMouseEnter(
                                appointment,
                                "appointment",
                                overlappingEvents
                              )
                            }
                            onMouseLeave={handleEventMouseLeave}
                            onClick={() => handleEventClick(appointment)}
                            className="group absolute flex flex-col overflow-y-auto rounded-sm px-1.5 py-1 text-xs/5 cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-offset-1 hover:ring-gray-300"
                            style={{
                              backgroundColor: `${appointment.clinician.calendarColor}20`,
                              width,
                              left,
                              top: "0px",
                              bottom: "0px",
                              zIndex: 2,
                              position: "absolute",
                            }}>
                            <p
                              style={{
                                color:
                                  appointment.clinician.calendarColor ||
                                  "#3b82f6",
                              }}>
                              <time dateTime={appointment.start}>
                                {format(localStart, "h:mm a")}
                              </time>
                              <strong>
                                {" - "}
                                {appointment.clinician.calendarDisplayName}
                              </strong>
                            </p>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                color: appointment.clinician.calendarColor
                                  ? appointment.clinician.calendarColor
                                  : "#3b82f6",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}>
                              {appointment.blockNumber && (
                                <span>Block #{appointment.blockNumber}- </span>
                              )}
                              <span>{appointment.title}</span>
                            </div>
                            <p
                              style={{
                                color: appointment.clinician.calendarColor
                                  ? appointment.clinician.calendarColor
                                  : "#3b82f6",
                              }}>
                              {appointment.client?.firstName}{" "}
                              {appointment.client?.lastName}
                            </p>
                          </span>
                          {hoveredItem &&
                            hoveredItem.data._id === appointment._id &&
                            isTooltipVisible && (
                              <div
                                className="enhanced-tooltip"
                                style={{
                                  position: "absolute",
                                  width: "100%",
                                  zIndex: 50,
                                }}
                                onMouseEnter={() => {
                                  if (tooltipTimeout) {
                                    clearTimeout(tooltipTimeout);
                                  }
                                  setIsTooltipVisible(true);
                                  setHoveredItem({
                                    type: "appointment",
                                    data: appointment,
                                    overlappingEvents,
                                  });
                                }}
                                onMouseLeave={handleEventMouseLeave}>
                                <EnhancedTooltip
                                  mainEvent={hoveredItem.data}
                                  overlappingEvents={
                                    hoveredItem.overlappingEvents
                                  }
                                  onEventHover={(event) => {
                                    setHoveredItem({
                                      type:
                                        "client" in event
                                          ? "appointment"
                                          : "meeting",
                                      data: event,
                                      overlappingEvents:
                                        hoveredItem.overlappingEvents,
                                    });
                                  }}
                                  onEventLeave={() => {}}
                                  onEventClick={handleEventClick}
                                />
                              </div>
                            )}
                        </li>
                      );
                    })}

                  {/* Render meetings */}
                  {filteredMeetings
                    .filter(
                      (meeting) =>
                        selectedClinicians.length === 0 ||
                        meeting.attendees.some((attendee) =>
                          selectedClinicians.some((c) => c._id === attendee._id)
                        )
                    )
                    .filter((meeting) => {
                      // Filter out meetings that don't belong to the current week
                      if (!meeting.date) return false;

                      const meetingStart = toDate(parseISO(meeting.date), {
                        timeZone:
                          Intl.DateTimeFormat().resolvedOptions().timeZone,
                      });
                      const meetingDate = format(meetingStart, "yyyy-MM-dd");
                      const currentWeek = getCurrentWeek(currentDate);

                      // Check if meeting date is within the current week range
                      const weekStart = format(currentWeek[0], "yyyy-MM-dd");
                      const weekEnd = format(currentWeek[6], "yyyy-MM-dd");

                      return meetingDate >= weekStart && meetingDate <= weekEnd;
                    })
                    .map((meeting) => {
                      // Validate date and time
                      if (
                        !meeting.date ||
                        !meeting.startTime ||
                        meeting.startTime === "Invalid Date"
                      ) {
                        console.error(
                          "Invalid meeting date or start time:",
                          meeting
                        );
                        return null; // Skip rendering invalid meetings
                      }

                      const { startRow, span } = calculateMeetingGridPosition(
                        meeting.date,
                        meeting.startTime,
                        meeting.duration
                      );

                      const { width, left, overlappingEvents } =
                        calculateEventPosition(meeting, [
                          ...filteredAppointments,
                          ...filteredMeetings,
                        ]);

                      const localStart = toDate(parseISO(meeting.date), {
                        timeZone:
                          Intl.DateTimeFormat().resolvedOptions().timeZone,
                      });

                      // Get the day of week (1-7) for the meeting
                      const dayOfWeek = localStart.getDay() + 1; // Sunday will be column 1, Saturday will be column 7

                      return (
                        <li
                          key={meeting._id}
                          className="relative flex"
                          style={{
                            gridRow: `${startRow} / span ${span}`,
                            gridColumnStart: dayOfWeek,
                            position: "relative",
                          }}>
                          <span
                            onMouseEnter={(e) =>
                              handleEventMouseEnter(
                                meeting,
                                "meeting",
                                overlappingEvents
                              )
                            }
                            onMouseLeave={handleEventMouseLeave}
                            onClick={() => handleEventClick(meeting)}
                            className="group absolute flex flex-col overflow-y-auto rounded-sm px-1.5 py-1 text-xs/5 cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-offset-1 hover:ring-gray-300"
                            style={{
                              backgroundColor: "#6B728020",
                              border: "1px dashed #6B7280",
                              width,
                              left,
                              top: "0px",
                              bottom: "0px",
                              zIndex: 1,
                              position: "absolute",
                            }}>
                            <p className="font-semibold text-gray-700">
                              <time
                                dateTime={`${meeting.date.split("T")[0]}T${
                                  meeting.startTime
                                }`}>
                                {format(
                                  parseISO(
                                    `${meeting.date.split("T")[0]}T${
                                      meeting.startTime
                                    }`
                                  ),
                                  "h:mm a"
                                )}
                              </time>
                              {" - Meeting"}
                            </p>
                            <p className="text-gray-600">{meeting.title}</p>
                            <p className="text-gray-600">
                              Attendees:{" "}
                              {meeting.attendees
                                .map((a) => `${a.firstName} ${a.lastName}`)
                                .join(", ")}
                            </p>
                            {meeting.description && (
                              <p className="text-gray-600 italic">
                                {meeting.description}
                              </p>
                            )}
                          </span>
                          {hoveredItem &&
                            hoveredItem.data._id === meeting._id &&
                            isTooltipVisible && (
                              <div
                                className="enhanced-tooltip"
                                style={{
                                  position: "absolute",
                                  width: "100%",
                                  zIndex: 50,
                                }}
                                onMouseEnter={() => {
                                  if (tooltipTimeout) {
                                    clearTimeout(tooltipTimeout);
                                  }
                                  setIsTooltipVisible(true);
                                  setHoveredItem({
                                    type: "meeting",
                                    data: meeting,
                                    overlappingEvents,
                                  });
                                }}
                                onMouseLeave={handleEventMouseLeave}>
                                <EnhancedTooltip
                                  mainEvent={hoveredItem.data}
                                  overlappingEvents={
                                    hoveredItem.overlappingEvents
                                  }
                                  onEventHover={(event) => {
                                    setHoveredItem({
                                      type:
                                        "client" in event
                                          ? "appointment"
                                          : "meeting",
                                      data: event,
                                      overlappingEvents:
                                        hoveredItem.overlappingEvents,
                                    });
                                  }}
                                  onEventLeave={() => {}}
                                  onEventClick={handleEventClick}
                                />
                              </div>
                            )}
                        </li>
                      );
                    })}
                </ol>
              </div>
            </div>
          </div>

          {/* Quick Add Meeting Modal */}
          {isQuickAddMeetingOpen && (
            <QuickAddMeeting
              isOpen={isQuickAddMeetingOpen}
              onClose={() => setIsQuickAddMeetingOpen(false)}
              selectedMeeting={selectedEvent}
            />
          )}

          {/* Appointment Summary Modal */}
          {isAppointmentSummaryOpen && (
            <AppointmentSummary
              open={isAppointmentSummaryOpen}
              onClose={() => setIsAppointmentSummaryOpen(false)}
              // appointment={selectedEvent}
            />
          )}
        </div>
      </div>

      {/* Portal-rendered modals and notifications */}
      {showCalendarFeedModal &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="fixed inset-0" style={{ zIndex: 9999 }}>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/80"
              onClick={() => setShowCalendarFeedModal(false)}
            />

            {/* Modal Container */}
            <div className="fixed inset-0 flex items-center justify-center p-4">
              {/* Modal */}
              <div
                className="relative bg-white rounded-sm shadow-lg border w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}>
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-slate-100 rounded-sm flex items-center justify-center">
                        <svg
                          className="w-4 h-4 text-slate-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-slate-900">
                          Sync Your Calendar
                        </h3>
                        <p className="text-xs text-slate-500">
                          Add appointments to your phone's calendar
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowCalendarFeedModal(false)}
                      className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-sm hover:bg-slate-100">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {calendarFeedError ? (
                    <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                      <div className="flex items-center space-x-2">
                        <svg
                          className="w-4 h-4 text-red-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <p className="text-xs text-red-700 font-medium">
                          Error loading calendar feed
                        </p>
                      </div>
                      <p className="text-xs text-red-600 mt-1">
                        {calendarFeedError}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Calendar URL Section */}
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">
                          Calendar Subscription URL
                        </label>
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            readOnly
                            value={calendarFeedUrl || ""}
                            className="flex-1 px-2 py-1.5 border border-slate-300 rounded-sm bg-slate-50 text-xs text-slate-900 focus:ring-1 focus:ring-slate-500 focus:border-slate-500"
                          />
                          <button
                            onClick={() => {
                              handleCopyToClipboard(calendarFeedUrl || "");
                            }}
                            className="px-2 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium rounded-sm border border-slate-300 flex items-center space-x-1">
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                            <span className="hidden sm:inline">Copy</span>
                          </button>
                        </div>
                      </div>

                      {/* Instructions */}
                      <div>
                        <h4 className="text-xs font-medium text-slate-900 mb-2">
                          How to add to your calendar:
                        </h4>
                        <div className="space-y-2">
                          {/* iPhone/iPad */}
                          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
                            <div className="flex items-start space-x-2">
                              <div className="w-6 h-6 bg-blue-100 rounded-sm flex items-center justify-center flex-shrink-0">
                                <svg
                                  className="w-3 h-3 text-blue-600"
                                  fill="currentColor"
                                  viewBox="0 0 24 24">
                                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-blue-900">
                                  iPhone/iPad
                                </p>
                                <p className="text-xs text-blue-700 mt-0.5">
                                  Copy the URL above, then go to{" "}
                                  <strong>
                                    Settings → Calendar → Accounts → Add Account
                                    → Other → Add Subscribed Calendar
                                  </strong>
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Android */}
                          <div className="bg-green-50 border border-green-200 rounded-sm p-3">
                            <div className="flex items-start space-x-2">
                              <div className="w-6 h-6 bg-green-100 rounded-sm flex items-center justify-center flex-shrink-0">
                                <svg
                                  className="w-3 h-3 text-green-600"
                                  fill="currentColor"
                                  viewBox="0 0 24 24">
                                  <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-green-900">
                                  Android
                                </p>
                                <p className="text-xs text-green-700 mt-0.5">
                                  Open{" "}
                                  <strong>
                                    Google Calendar app → Settings → Add
                                    calendar → From URL
                                  </strong>
                                  , then paste the URL above
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Desktop */}
                          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                            <div className="flex items-start space-x-2">
                              <div className="w-6 h-6 bg-slate-100 rounded-sm flex items-center justify-center flex-shrink-0">
                                <svg
                                  className="w-3 h-3 text-slate-600"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                  />
                                </svg>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-slate-900">
                                  Desktop Calendar
                                </p>
                                <p className="text-xs text-slate-700 mt-0.5">
                                  Most calendar apps support importing via URL.
                                  Look for{" "}
                                  <strong>"Subscribe to calendar"</strong> or{" "}
                                  <strong>"Add calendar from URL"</strong>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Regenerate Section */}
                      <div className="border-t border-slate-200 pt-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-slate-700">
                              Regenerate
                            </p>
                            <p className="text-xs text-slate-500">
                              The old URL will stop working once regenerated.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={handleRegenerateCalendarFeedUrl}
                            disabled={calendarFeedLoading}
                            className="px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-sm hover:bg-amber-100 focus:ring-1 focus:ring-amber-500 disabled:opacity-50">
                            {calendarFeedLoading ? (
                              <div className="flex items-center space-x-1">
                                <div className="animate-spin rounded-full h-3 w-3 border border-amber-600 border-t-transparent"></div>
                                <span>Loading...</span>
                              </div>
                            ) : (
                              "Regenerate"
                            )}
                          </button>
                        </div>
                        {urlUpdated && (
                          <div className="mt-2 bg-green-50 border border-green-200 rounded-sm p-2">
                            <div className="flex items-center space-x-1">
                              <svg
                                className="w-3 h-3 text-green-600"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              <p className="text-xs text-green-700 font-medium">
                                URL regenerated successfully
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex gap-2 mt-4 pt-3 border-t border-slate-200">
                    <button
                      type="button"
                      className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-medium py-2 px-3 rounded-sm text-xs transition-colors"
                      onClick={() => setShowCalendarFeedModal(false)}>
                      Done
                    </button>
                    <button
                      type="button"
                      className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2 px-3 rounded-sm border border-slate-300 text-xs transition-colors"
                      onClick={() => {
                        handleCopyToClipboard(calendarFeedUrl || "");
                      }}>
                      Copy URL
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Toast Notification Portal */}
      {showCopyToast &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed bottom-4 right-4 animate-in fade-in slide-in-from-bottom-2 duration-300"
            style={{ zIndex: 10000 }}>
            <div className="bg-slate-900 text-white px-4 py-2 rounded-sm shadow-lg flex items-center space-x-2">
              <svg
                className="w-4 h-4 text-green-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span className="text-sm font-medium">Copied to clipboard</span>
            </div>
          </div>,
          document.body
        )}

      {/* Clinic Selection Modal */}
      {showClinicModal &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="fixed inset-0" style={{ zIndex: 9999 }}>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setShowClinicModal(false)}
            />

            {/* Modal Container */}
            <div className="fixed inset-0 flex items-center justify-center p-4">
              {/* Modal */}
              <div
                className="relative bg-white rounded-sm shadow-lg border w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}>
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Select Clinics
                    </h3>
                    <button
                      onClick={() => setShowClinicModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-sm hover:bg-gray-100">
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="space-y-3">
                    {/* Select All / Clear All */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => dispatch(setSelectedClinicsForSchedule(clinics))}
                        className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded-sm hover:bg-blue-100">
                        Select All
                      </button>
                      <button
                        onClick={() => dispatch(setSelectedClinicsForSchedule([]))}
                        className="px-3 py-1 text-sm bg-gray-50 text-gray-700 rounded-sm hover:bg-gray-100">
                        Clear All
                      </button>
                    </div>

                    {/* Clinic List */}
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {clinics.map((clinic) => (
                        <label
                          key={clinic._id}
                          className="flex items-center space-x-3 p-2 rounded-sm hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedClinicsForSchedule.some(c => c._id === clinic._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                dispatch(setSelectedClinicsForSchedule([...selectedClinicsForSchedule, clinic]));
                              } else {
                                dispatch(setSelectedClinicsForSchedule(
                                  selectedClinicsForSchedule.filter(c => c._id !== clinic._id)
                                ));
                              }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-900">{clinic.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setShowClinicModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50">
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Clinician Selection Modal */}
      {showClinicianModal &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="fixed inset-0" style={{ zIndex: 9999 }}>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50"
              onClick={() => setShowClinicianModal(false)}
            />

            {/* Modal Container */}
            <div className="fixed inset-0 flex items-center justify-center p-4">
              {/* Modal */}
              <div
                className="relative bg-white rounded-sm shadow-lg border w-full max-w-md mx-4 max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}>
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Select Clinicians
                    </h3>
                    <button
                      onClick={() => setShowClinicianModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-sm hover:bg-gray-100">
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="space-y-3">
                    {/* Select All / Clear All */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedClinicians(clinicians)}
                        className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded-sm hover:bg-blue-100">
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedClinicians([])}
                        className="px-3 py-1 text-sm bg-gray-50 text-gray-700 rounded-sm hover:bg-gray-100">
                        Clear All
                      </button>
                    </div>

                    {/* Clinician List */}
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {clinicians.map((clinician) => (
                        <label
                          key={clinician._id}
                          className="flex items-center space-x-3 p-2 rounded-sm hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedClinicians.some(c => c._id === clinician._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedClinicians([...selectedClinicians, clinician]);
                              } else {
                                setSelectedClinicians(
                                  selectedClinicians.filter(c => c._id !== clinician._id)
                                );
                              }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <div className="flex items-center space-x-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: clinician.calendarColor }}
                            />
                            <span className="text-sm text-gray-900">
                              {clinician.firstName} {clinician.lastName}
                            </span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => setShowClinicianModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50">
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
